import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Table,
  Upload,
  message,
  Space,
  Progress,
  Alert,
  Form,
  Input,
  Select
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  ImportOutlined
} from '@ant-design/icons';
import axios from '../../../../../utils/axios';
import styles from './index.module.css';

const { Title } = Typography;

const ExamManage = () => {
  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importConfig, setImportConfig] = useState({
    collectionName: 'economist_exam',
    category: '工商管理',
    examData: '2023年12月'
  });
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [questionList, setQuestionList] = useState([]);

  useEffect(() => {
    testConnection();
    fetchFileList();
    fetchDocuments();
    fetchQuestions();
  }, []);

  const fetchFileList = async () => {
    try {
      const response = await axios.get('/api/exam/files');
      if (response.data.files) {
        setFileList(response.data.files);
      }
    } catch (error) {
      console.error('获取文件列表失败:', error);
      message.error('获取文件列表失败');
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/documents');
      setDocuments(response.data);
    } catch (error) {
      message.error('获取文档列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/exam/economist_exam');
      
      if (response.data.success && response.data.data?.questions) {
        const questions = response.data.data.questions;
        if (questions.length === 0) {
          message.info('暂无试题数据');
        }
        setQuestionList(questions);
      } else {
        message.warning('获取试题数据失败');
      }
    } catch (error) {
      console.error('获取试题失败:', error);
      message.error('加载试题失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTestParse = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/exam/test-parse', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data.success) {
        message.success(`解析成功，找到 ${response.data.questionsCount} 道题目`);
        console.log('解析结果:', response.data.questions);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error('解析测试失败: ' + error.message);
    }
  };

  const renderConfigForm = () => {
    return (
      <Form
        layout="vertical"
        initialValues={importConfig}
        onValuesChange={(_, allValues) => setImportConfig(allValues)}
      >
        <Form.Item
          label="数据库集合名称"
          name="collectionName"
          rules={[{ required: true, message: '请输入集合名称' }]}
        >
          <Input 
            placeholder="请输入集合名称，默认为 economist_exam"
            defaultValue="economist_exam"
          />
        </Form.Item>

        <Form.Item
          label="题目分类"
          name="category"
          rules={[{ required: true, message: '请输入题目分类' }]}
        >
          <Input 
            placeholder="请输入题目分类，如：工商管理"
            defaultValue="工商管理"
          />
        </Form.Item>

        <Form.Item
          label="考试日期"
          name="examData"
          rules={[{ required: true, message: '请输入考试日期' }]}
        >
          <Input 
            placeholder="请输入考试日期，如：2023年12月"
            defaultValue="2023年12月"
          />
        </Form.Item>
      </Form>
    );
  };

  const handleImport = async (filename) => {
    try {
      setImporting(true);
      console.log('开始导入文件:', filename, '配置:', importConfig);

      const response = await axios.post('/api/exam/import', {
        filename,
        config: importConfig
      });

      if (response.data.success) {
        const { totalQuestions, insertedCount, collection } = response.data.data || {};
        
        if (typeof insertedCount === 'number') {
          message.success(
            `成功导入 ${insertedCount} 道试题到 ${collection} 集合`
          );
          
          if (insertedCount < totalQuestions) {
            message.warning(
              `注意：共 ${totalQuestions} 道题目，` +
              `成功导入 ${insertedCount} 道，` +
              `${totalQuestions - insertedCount} 道题目未能导入`
            );
          }
        } else {
          message.success(response.data.message || '导入成功');
        }
        
        fetchFileList(); // 刷新文件列表
      } else {
        console.error('导入失败:', response.data);
        message.error(response.data.message || '导入失败');
      }
    } catch (error) {
      console.error('导入错误:', error);
      message.error(
        error.response?.data?.message || 
        error.message || 
        '导入失败，请检查文件格式'
      );
    } finally {
      setImporting(false);
    }
  };

  const uploadProps = {
    name: 'file',
    action: '/api/exam/upload',
    accept: '.json',
    showUploadList: true,
    maxCount: 1,
    headers: {
      'X-Requested-With': null
    },
    withCredentials: true,
    beforeUpload: (file) => {
      const isJson = file.type === 'application/json' || 
                    file.name.endsWith('.json');
      if (!isJson) {
        message.error('只支持上传 JSON 文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('文件必须小于 20MB！');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        setSelectedFile(info.file.response.filename);
        fetchFileList();
      }
    }
  };

  const handleDelete = async (filename) => {
    try {
      await axios.delete(`/api/exam/file/${filename}`);
      message.success('文件删除成功');
      await fetchFileList();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const handlePreview = (filename) => {
    window.open(`/exam/practice`, '_blank');
  };

  const columns = [
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
    },
    {
      title: '上传时间',
      dataIndex: 'uploadTime',
      key: 'uploadTime',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ 
          color: status === 'completed' ? '#52c41a' : 
                 status === 'pending' ? '#faad14' : '#f5222d'
        }}>
          {status === 'completed' ? '已导入' : 
           status === 'pending' ? '待导入' : '导入失败'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {record.status === 'pending' && (
            <Button
              type="primary"
              icon={<ImportOutlined />}
              loading={importing && selectedFile === record.filename}
              onClick={() => handleImport(record.filename)}
            >
              导入
            </Button>
          )}
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record.filename)}
          >
            预览
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.filename)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const testConnection = async () => {
    try {
      const response = await axios.get('/api/exam/economist_exam/test');
      if (response.data.success) {
        message.success('连接测试成功');
      } else {
        message.warning(response.data.message || '连接测试失败');
      }
    } catch (error) {
      console.error('连接测试错误:', error);
      message.error('连接测试失败: ' + (error.response?.data?.message || '服务器错误'));
    }
  };

  return (
    <div className={styles.manageContainer}>
      <Card className={styles.manageCard}>
        <Title level={3}>试题管理</Title>
        
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {renderConfigForm()}
          
          <div className={styles.uploadSection}>
            <Upload {...uploadProps}>
              <Button 
                type="primary"
                icon={<UploadOutlined />}
                loading={uploading}
              >
                上传试题文档
              </Button>
            </Upload>
          </div>

          {uploading && (
            <Progress percent={progress} status="active" />
          )}
          
          {error && (
            <Alert
              message="上传失败"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}
          
          <Table
            columns={columns}
            dataSource={fileList}
            rowKey="filename"
            pagination={false}
            loading={loading}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ExamManage; 